import PropTypes from 'prop-types';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon, PlusIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import AutocompleteInput from '../../autocomplete-input';
import { TagsMultiSelect } from '../../tags-multi-select';
import { useNotificationsProvider } from '../../../context/NotificationsContext';
import DynamicFieldSelectorDialog from '../../dynamic-field-selector';
import DynamicFieldValueComponent from '../../dynamic-field-value';
import {
  useCreateResourceMutation,
  useSetTagsForEntityMutation,
} from '../../../services/api';

AddResourceDialog.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onCreated: PropTypes.func,
};

export default function AddResourceDialog({ title, description, open, onClose, onCreated }) {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();
  const [createResource, { isLoading: isCreating }] = useCreateResourceMutation();
  const [setTags, { isLoading: isSettingTags }] = useSetTagsForEntityMutation();

  const [openDynamicFieldSelector, setOpenDynamicFieldSelector] = useState(false);
  const [selectedDynamicField, setSelectedDynamicField] = useState(null);

  const loading = isCreating || isSettingTags;

  const schema = z.object({
    name: z
      .string()
      .trim()
      .min(1, t('resource.errors.name-required', 'Name is required')),
    selectedTags: z.array(z.any()),
    dynamicFields: z.array(z.any()),
  });

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      selectedTags: [],
      dynamicFields: [],
    },
  });

  const watchDynamicFields = form.watch('dynamicFields');

  const resetState = () => {
    form.reset();
    setSelectedDynamicField(null);
  };

  const onOpenChange = (next) => {
    if (!next) {
      if (loading) return;
      resetState();
      onClose?.();
    }
  };

  const onFieldAdded = (data) => {
    setOpenDynamicFieldSelector(false);
    const current = form.getValues('dynamicFields');
    if (selectedDynamicField) {
      const next = current.map((field) =>
        field === selectedDynamicField ? data : field,
      );
      form.setValue('dynamicFields', next);
      setSelectedDynamicField(null);
      return;
    }
    form.setValue('dynamicFields', [...current, data]);
  };

  const onEditField = (data) => {
    setSelectedDynamicField(data);
    setOpenDynamicFieldSelector(true);
  };

  const onDeleteField = (data) => {
    const current = form.getValues('dynamicFields');
    form.setValue('dynamicFields', current.filter((field) => field !== data));
  };

  const onSubmit = async (values) => {
    try {
      const created = await createResource({
        name: values.name.trim(),
        dynamicData: values.dynamicFields,
      }).unwrap();
      if (values.selectedTags.length > 0 && created?.id) {
        await setTags({
          taggableType: 'Resource',
          taggableId: created.id,
          tagIds: values.selectedTags.map((tag) => tag.id),
        }).unwrap();
      }
      resetState();
      onCreated?.();
    } catch {
      showNotification('error', t('common.api-error'));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          onInteractOutside={(e) => { if (loading) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (loading) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>{title ?? t('resource.add-resource-dialog.title')}</DialogTitle>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-5"
              noValidate
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t('resource.add-resource-dialog.name-title')}
                    </FormLabel>
                    <FormControl>
                      <AutocompleteInput
                        error={fieldState.error ? 'required' : undefined}
                        onChange={(val) => field.onChange(val)}
                        resource="resource/name-autocomplete"
                        required
                        canDelete
                        refreshOnInputChange
                        clearOnBlur={false}
                        freeSolo
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="selectedTags"
                render={({ field }) => (
                  <FormItem>
                    <TagsMultiSelect
                      value={field.value}
                      onChange={field.onChange}
                      label={t('resource.add-resource-dialog.tags-title')}
                      placeholder={t('tags.multi-select.placeholder')}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {watchDynamicFields?.length > 0 ? (
                <div className="space-y-3">
                  {watchDynamicFields.map((field) => (
                    <DynamicFieldValueComponent
                      key={`${field.title}-${field.dataType}-${field.value}`}
                      value={field}
                      onEdit={onEditField}
                      onDelete={onDeleteField}
                    />
                  ))}
                </div>
              ) : null}

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpenDynamicFieldSelector(true)}
                className="text-primary"
              >
                <PlusIcon className="size-4" />
                {t('resource.add-resource-dialog.add-field')}
              </Button>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={loading}
                >
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                  {t('common.save')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <DynamicFieldSelectorDialog
        open={openDynamicFieldSelector}
        onClose={() => {
          setSelectedDynamicField(null);
          setOpenDynamicFieldSelector(false);
        }}
        onCreated={onFieldAdded}
        title={t('components.dynamic-field-selector.title')}
        value={selectedDynamicField}
      />
    </>
  );
}
